import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import posed, { PoseGroup } from 'react-pose';
import { Button, Menu, Shelf, Spinner, WarningMessage, WizardFooter } from '../../components/widgets';
import PlusCircleIcon from '../../resources/svg/add.svg';
import { AUDIENCE_DATA_EXTENDED_RETENTION_HOURS, FILTER_TYPES, TIMES, TIMES_EXTENDED } from '../consts';
import { GET_ORGANIZATION_OPTIONS } from '../queries';
import Details from './Details';
import css from './Edit.pcss';
import Filter from './Filter';
import Settings from './Settings';

const AnimationItem = posed.div({
    enter: {
        opacity: 1,
        y: 0,
        delay: ({ i }) => {
            return i * 50;
        },
    },
    exit: {
        y: 20,
        opacity: 0,
    },
});

function moveToView(element, parent, container) {
    const footerHeight = 84;
    // Get container properties
    const cTop = container.scrollTop;
    const cBottom = cTop + container.clientHeight - footerHeight;

    // Get element properties
    const eTop = element.offsetTop;
    const pTop = parent.offsetTop;
    const totalTop = eTop + pTop;
    const eBottom = totalTop + element.clientHeight;

    const elementVisible = totalTop >= cTop && eBottom <= cBottom;
    // Check if in view
    if (!elementVisible) {
        container.scrollTop += eBottom - cBottom + footerHeight;
    }
}

const AudienceSegmentsEdit = ({
    editId = null,
    edit = null,
    editInvalidMsg = false,
    editDirty = false,
    metadata = {},
    saving = false,
    readOnly = false,
    onEditConfirm = () => {},
    onEditCancel = () => {},
    onEditDetails = () => {},
    onAddFilter = () => {},
    onEditFilter = () => {},
    onDeleteFilter = () => {},
    onAddCondition = () => {},
    onEditCondition = () => {},
    onDeleteCondition = () => {},
    translate = () => {},
}) => {
    const editContainer = useRef(null);
    const contentRef = useRef(null);
    const filterRefs = useRef({});

    const { data: organizationOptions } = useQuery(GET_ORGANIZATION_OPTIONS, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: false,
    });

    const useExtendedAudienceRetention = useMemo(() => {
        if (organizationOptions?.audiencesRetention) {
            const retentionHours = parseInt(organizationOptions.audiencesRetention.replace('h', ''), 10);
            return retentionHours >= AUDIENCE_DATA_EXTENDED_RETENTION_HOURS;
        }
        return false;
    }, [organizationOptions?.audiencesRetention]);

    const timesList = useExtendedAudienceRetention ? TIMES_EXTENDED : TIMES;

    const buttons = {
        cancel: {
            text: 'Cancel',
            disabled: false,
            type: 'flat',
        },
        finish: {
            text: 'Finish',
            type: 'primary',
        },
    };

    const editConfirm = () => onEditConfirm(editId, edit);

    const addFilter = filterType => {
        onAddFilter(filterType);
        window.setTimeout(() => {
            editContainer.current.scrollTop = editContainer.current.scrollHeight;
        }, 0);
    };

    const addCondition = index => {
        setTimeout(() => {
            const filterRef = filterRefs.current[`filter_${index}`];
            const lastCondition = filterRef.container.current.querySelector(`.${css.condition}:last-child`);
            moveToView(lastCondition, filterRef.container.current, editContainer.current);
        }, 50);
        const filterType = edit.filters[index].filter_type;
        onAddCondition(index, filterType);
    };

    const editCancel = () => {
        if (editDirty) {
            if (!confirm(translate('STATIC.PAGES.AUDIENCES.UNSAVED_MESSAGE'))) {
                return;
            }
        }
        onEditCancel(editId);
    };

    const getFooterButtons = () => {
        const footerButtons = [
            <Button {...buttons.cancel} onClick={editCancel}>
                <Translate id={readOnly ? 'STATIC.BUTTONS.CLOSE' : 'STATIC.BUTTONS.CANCEL'} />
            </Button>,
        ];
        if (!readOnly) {
            footerButtons.push(
                <Button {...buttons.finish} onClick={editConfirm} disabled={!editDirty || !!editInvalidMsg}>
                    <Translate id={editId === -1 ? 'STATIC.BUTTONS.SAVE' : 'STATIC.BUTTONS.UPDATE'} />
                </Button>
            );
        }
        return footerButtons;
    };

    const getHeaderText = () => {
        if (readOnly) return 'STATIC.PAGES.AUDIENCES.VIEW_SEGMENT';
        if (editId === -1) return 'STATIC.PAGES.AUDIENCES.NEW_SEGMENT';
        return 'STATIC.PAGES.AUDIENCES.EDIT_SEGMENT';
    };

    let conditions = 0;
    return (
        <Shelf
            contentRef={e => {
                contentRef.current = e;
            }}
            open={!!editId}
            headerText={getHeaderText()}
            onClose={editCancel}
            shelfSize="medium"
        >
            {!edit || saving ? (
                <Spinner show expanded />
            ) : (
                <div className={css.container}>
                    <WarningMessage
                        show={!metadata && !editInvalidMsg}
                        showIcon={false}
                        message="STATIC.PAGES.AUDIENCES.EDIT_INVALID_WARNING"
                    />
                    <WarningMessage
                        show={!!editInvalidMsg}
                        message={editInvalidMsg || ''}
                        showIcon={false}
                        type="error"
                    />
                    <div className={css.editContainer} ref={editContainer}>
                        <div className={css.edit}>
                            <div className={css.line} />
                            <div>
                                <div className={css.dot} />
                                <Details
                                    edit={edit}
                                    invalid={!!editInvalidMsg}
                                    metadata={metadata}
                                    onChange={onEditDetails}
                                    disabled={readOnly}
                                />
                                <Settings
                                    edit={edit}
                                    invalid={!!editInvalidMsg}
                                    metadata={metadata}
                                    onChange={onEditDetails}
                                    disabled={readOnly}
                                />
                                <PoseGroup animateOnMount flipMove={false}>
                                    {edit.filters &&
                                        edit.filters.map((filter, i) => (
                                            <AnimationItem
                                                key={`filter_${filter.filter_type}_${filter.id}`}
                                                i={conditions++}
                                            >
                                                <Filter
                                                    index={i}
                                                    filter={filter}
                                                    metadata={metadata}
                                                    editFilter={onEditFilter}
                                                    deleteFilter={onDeleteFilter}
                                                    editCondition={onEditCondition}
                                                    addCondition={addCondition}
                                                    deleteCondition={onDeleteCondition}
                                                    apps={edit.apps}
                                                    invalid={!!editInvalidMsg}
                                                    timesList={timesList}
                                                    disabled={readOnly}
                                                    ref={el => {
                                                        filterRefs.current[`filter_${i}`] = el;
                                                    }}
                                                />
                                            </AnimationItem>
                                        ))}
                                </PoseGroup>
                                {!readOnly && (
                                    <div className={css.addFilter}>
                                        <div className={classNames(css.dot, css.bottomDot)} />
                                        <div className={css.dottedLine} />
                                        <div className={css.addDot}>
                                            <PlusCircleIcon style={{ width: 17 }} className={css.addDotIcon} />
                                        </div>
                                        <Menu
                                            containerClass={css.addFilterDropdown}
                                            buttonClass={css.addFilterDropdownButton}
                                            buttonLabel="Add a Filter"
                                            buttonIconStyle={{
                                                height: '10px',
                                                marginLeft: '4px',
                                                marginTop: '1px',
                                            }}
                                            items={Object.keys(FILTER_TYPES).map(f => ({
                                                value: FILTER_TYPES[f],
                                                label: `${FILTER_TYPES[f].charAt(0).toUpperCase() +
                                                    FILTER_TYPES[f].substr(1)} Filter`,
                                                onClick: () => addFilter(FILTER_TYPES[f]),
                                            }))}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <WizardFooter buttons={getFooterButtons()} />
        </Shelf>
    );
};

AudienceSegmentsEdit.propTypes = {
    editId: PropTypes.number,
    edit: PropTypes.objectOf(PropTypes.any),
    editInvalidMsg: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    editDirty: PropTypes.bool,
    saving: PropTypes.bool,
    readOnly: PropTypes.bool,
    metadata: PropTypes.objectOf(PropTypes.any),
    onEditConfirm: PropTypes.func,
    onEditCancel: PropTypes.func,
    onEditDetails: PropTypes.func,
    onAddFilter: PropTypes.func,
    onEditFilter: PropTypes.func,
    onDeleteFilter: PropTypes.func,
    onAddCondition: PropTypes.func,
    onEditCondition: PropTypes.func,
    onDeleteCondition: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(AudienceSegmentsEdit);
